/* eslint-disable new-cap */
/**
 * REST API を使用した配信スケジュール取得 hook
 * OpenAPI仕様から生成された型を使用（SSOT）
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { components } from "../api/schema";

// =====================================
// 生成された型を再エクスポート
// =====================================

export type DeliverySchedule = components["schemas"]["DeliverySchedule"];
export type DeliverySchedulesResponse =
  components["schemas"]["DeliverySchedulesResponse"];
export type DeliveryScheduleSearchInput =
  components["schemas"]["DeliveryScheduleSearchInput"];

// DeliverySchedules型（既存コードとの互換性）
export interface DeliverySchedules {
  totalCount: number;
  nodes: DeliverySchedule[];
}

// =====================================
// REST API Hook Types
// =====================================

export interface UseDeliverySchedulesRestApiOptions {
  variables?: {
    filter?: DeliveryScheduleSearchInput | null;
    offset?: number;
    limit?: number;
  };
  onCompleted?: () => void;
}

export interface UseDeliverySchedulesRestApiResult {
  data: { deliverySchedules: DeliverySchedules } | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: (variables?: {
    filter?: DeliveryScheduleSearchInput | null;
    offset?: number;
    limit?: number;
  }) => Promise<{ deliverySchedules: DeliverySchedules }>;
}

/**
 * 配信スケジュール一覧を REST API から取得する hook
 * @param {UseDeliverySchedulesRestApiOptions} options - フック設定オプション
 * @return {UseDeliverySchedulesRestApiResult} 配信スケジュールデータ、エラー、ローディング状態、再取得関数を含むオブジェクト
 */
export function useDeliverySchedulesRestApi(
  options: UseDeliverySchedulesRestApiOptions = {},
): UseDeliverySchedulesRestApiResult {
  const [data, setData] = useState<
    { deliverySchedules: DeliverySchedules } | undefined
  >(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchDeliverySchedules = useCallback(
    async (variables?: {
      filter?: DeliveryScheduleSearchInput | null;
      offset?: number;
      limit?: number;
    }): Promise<{ deliverySchedules: DeliverySchedules }> => {
      setLoading(true);
      setError(undefined);

      try {
        const params: Record<string, string> = {};

        const vars = variables || options.variables;
        if (vars?.offset !== undefined) {
          params.offset = String(vars.offset);
        }
        if (vars?.limit !== undefined) {
          params.limit = String(vars.limit);
        }
        if (vars?.filter) {
          params.filter = JSON.stringify(vars.filter);
        }

        const { data: responseData, error: apiError } = await api.GET(
          "/api/delivery/schedules",
          {
            params: {
              query: Object.keys(params).length > 0 ? params : undefined,
            },
          },
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || "API error",
          );
        }

        if (!responseData) {
          throw new Error("No data received");
        }

        const result = {
          deliverySchedules: {
            totalCount: responseData.totalCount,
            nodes: responseData.nodes,
          },
        };

        setData(result);
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
        options.onCompleted?.();
      }
    },
    [options.onCompleted, options.variables],
  );

  const initialFetchDone = useRef(false);
  const variablesRef = useRef(options.variables);

  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchDeliverySchedules(variablesRef.current).catch(() => {
        // エラーは state で管理
      });
    }
  }, [fetchDeliverySchedules]);

  const refetch = useCallback(
    async (variables?: {
      filter?: DeliveryScheduleSearchInput | null;
      offset?: number;
      limit?: number;
    }): Promise<{ deliverySchedules: DeliverySchedules }> => {
      return fetchDeliverySchedules(variables || options.variables);
    },
    [fetchDeliverySchedules, options.variables],
  );

  return {
    data,
    error,
    loading,
    refetch,
  };
}

export default useDeliverySchedulesRestApi;
